import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { ImageOff, RadioTower } from 'lucide-react'
import { usePlayoutState } from '../../store/index.js'
import { usePlayout } from '../../hooks/usePlayout.js'
import { cgTemplatesApi, logoProfilesApi } from '../../lib/api.js'
import type { CgTemplate, LogoProfile, ScheduleItem } from '../../types/index.js'

export const BrandingPanel = memo(function BrandingPanel({
  channelId, onAirItem,
}: {
  channelId: string | null
  onAirItem: ScheduleItem | null
}) {
  const playout = usePlayoutState()
  const { setBug, activeBugType, bugPending } = usePlayout(channelId)
  const [logoProfiles, setLogoProfiles] = useState<LogoProfile[]>([])
  const [templates, setTemplates] = useState<CgTemplate[]>([])
  const [pendingTemplateId, setPendingTemplateId] = useState<string | null>(null)
  const [error, setError] = useState('')

  const bridgeConnected = playout?.bridgeConnected ?? false
  // Un envoi a la main est identifie par l'id du modele, pas d'une regle.
  const activeTemplateIds = useMemo(() => new Set(
    (playout?.activeCgOverlays ?? [])
      .map(overlay => (typeof overlay.id === 'string' ? overlay.id : null))
      .filter((id): id is string => Boolean(id)),
  ), [playout?.activeCgOverlays])

  // Les habillages manuels sont déclenchables pendant un programme, un direct
  // ou une plage d'infoservice : dans ces trois cas CasparCG a une image de fond
  // à incruster. Sur infoservice l'habillage couvre toute la plage, pas le seul
  // segment courant.
  const canShowManualCg = Boolean(onAirItem && ['PROGRAM', 'LIVE', 'INFOSERVICE'].includes(onAirItem.itemType))

  useEffect(() => {
    if (!channelId) { setLogoProfiles([]); return }
    let cancelled = false
    const load = () => {
      logoProfilesApi.list(channelId)
        .then(profiles => { if (!cancelled) setLogoProfiles(profiles.filter(profile => profile.active && profile.manualOnGrid)) })
        .catch(() => { if (!cancelled) setLogoProfiles([]) })
    }
    load()
    const onUpdated = (event: Event) => {
      const updatedChannelId = (event as CustomEvent<string | undefined>).detail
      if (!updatedChannelId || updatedChannelId === channelId) load()
    }
    window.addEventListener('trafficplay:logo-profiles-updated', onUpdated)
    return () => {
      cancelled = true
      window.removeEventListener('trafficplay:logo-profiles-updated', onUpdated)
    }
  }, [channelId])

  useEffect(() => {
    if (!channelId) { setTemplates([]); return }
    let cancelled = false
    const load = async () => {
      try {
        const next = await cgTemplatesApi.list(channelId)
        if (cancelled) return
        // La coche « afficher sur la grille » de la roue crantee du modele est
        // le seul moyen d'apparaitre ici.
        setTemplates(next.filter(template => template.active && template.manualOnGrid))
        setError('')
      } catch {
        if (!cancelled) setError('Habillages manuels indisponibles')
      }
    }
    void load()
    const timer = setInterval(load, 30_000)
    window.addEventListener('focus', load)
    return () => {
      cancelled = true
      clearInterval(timer)
      window.removeEventListener('focus', load)
    }
  }, [channelId])

  const toggleTemplate = useCallback(async (template: CgTemplate) => {
    if (!channelId || pendingTemplateId) return
    const isActive = activeTemplateIds.has(template.id)
    if (!isActive && (!bridgeConnected || !canShowManualCg)) return
    setPendingTemplateId(template.id)
    setError('')
    try {
      if (isActive) await cgTemplatesApi.hide(channelId, template.id)
      else await cgTemplatesApi.show(channelId, template.id)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Commande CG impossible')
    } finally {
      setPendingTemplateId(null)
    }
  }, [activeTemplateIds, bridgeConnected, canShowManualCg, channelId, pendingTemplateId])

  const handleSelectLogo = useCallback((id: string) => { void setBug(id) }, [setBug])

  return (
    <div className="tp-airbanner__branding">
      <div className="tp-airbanner__eyebrow">HABILLAGE ANTENNE</div>

      <div className="tp-airbanner__logo-row">
        {logoProfiles.length === 0 && <span className="tp-airbanner__hint">Aucun profil de logo actif</span>}
        {/* « Aucun » est l'interrupteur, pas un choix d'habillage : il passe en
            bouton-icone pour laisser toute la largeur aux profils nommes. Sur
            une chaine a trois profils, les libelles n'avaient plus que 55 px et
            se lisaient « LOGO… ». */}
        <button
          type="button"
          className={`tp-airbanner__logo-button is-none${(activeBugType ?? 'NONE') === 'NONE' ? ' is-active' : ''}`}
          disabled={!bridgeConnected || bugPending}
          onClick={() => handleSelectLogo('NONE')}
          aria-pressed={(activeBugType ?? 'NONE') === 'NONE'}
          title="Retirer le logo antenne"
          aria-label="Retirer le logo antenne"
        >
          <ImageOff size={14} aria-hidden="true" />
        </button>

        {logoProfiles.map(profile => {
          const isActive = activeBugType === profile.id
          return (
            <button
              key={profile.id}
              type="button"
              className={`tp-airbanner__logo-button${isActive ? ' is-active' : ''}`}
              disabled={!bridgeConnected || bugPending}
              onClick={() => handleSelectLogo(profile.id)}
              aria-pressed={isActive}
              title={`Afficher ${profile.name}`}
            >
              <span className="tp-airbanner__logo-name">{profile.name.toUpperCase()}</span>
            </button>
          )
        })}
      </div>

      {templates.length > 0 && (
        <div className="tp-airbanner__cg-row">
          {templates.map(template => {
            const isActive = activeTemplateIds.has(template.id)
            const pending  = pendingTemplateId === template.id
            const disabled = pending || !bridgeConnected || (!isActive && !canShowManualCg)
            const title = !bridgeConnected
              ? 'CasparCG non connecté'
              : !isActive && !canShowManualCg
                ? 'Disponible pendant un programme, un direct ou une plage d’infoservice'
                : isActive ? `Masquer ${template.name}` : `Afficher ${template.name}`
            return (
              <button
                key={template.id}
                type="button"
                className={`tp-airbanner__cg-button${isActive ? ' is-active' : ''}`}
                disabled={disabled}
                aria-pressed={isActive}
                title={title}
                onClick={() => { void toggleTemplate(template) }}
              >
                <RadioTower size={12} aria-hidden="true" />
                <span className="tp-airbanner__cg-name">{template.name}</span>
                <span className="tp-airbanner__cg-state">
                  {pending ? '…' : isActive ? 'RETIRER' : 'FIN'}
                </span>
              </button>
            )
          })}
        </div>
      )}

      {error && <div className="tp-airbanner__error">{error}</div>}
    </div>
  )
})
